import * as Yup from 'yup';

import { BRANCH_TYPE, EIV_CHAR_LENGTH, TENANT_ID, VALIDATION_CONSTANTS } from 'global/constants';

import {
  doesRegisteredInstallerHaveBranch,
  isRegisteredBranchGuest,
  isRegisteredInstallerGuestOnly,
  isRegisteredInstallerOnlyHilfsbetrieb,
} from 'utils/InstallerUtils';
import { YDate, YDateRequired, YPhoneNumber, YZipRequired } from 'utils/YupUtils';
import { getTenantId } from 'utils/tenantUtil';

import { YExpert } from './YExpert';
// import { YContract } from './YContract';
import { YSeals } from './YSeals';

export const YDebFormSchema = (installerStatusSummary, doesNeedAvaGuestPostal) =>
  Yup.object().shape({
    companyName1: Yup.string()
      .required(VALIDATION_CONSTANTS.MANDATORY)
      .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
      .max(EIV_CHAR_LENGTH.FIRMENNAME1, VALIDATION_CONSTANTS.TOO_LONG),
    companyName2: Yup.lazy((value) => {
      if (value !== undefined && value !== '') {
        return Yup.string()
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.FIRMENNAME2, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    companyName3: Yup.lazy((value) => {
      if (value !== undefined && value !== '') {
        return Yup.string()
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.FIRMENNAME2, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    companyOwner: Yup.lazy(() => {
      if (TENANT_ID.BAG === getTenantId()) {
        return Yup.string()
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.FIRMENNAME3, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    companyStreet: Yup.string()
      .required(VALIDATION_CONSTANTS.MANDATORY)
      .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
      .max(EIV_CHAR_LENGTH.BUERO_STRASSE, VALIDATION_CONSTANTS.TOO_LONG),
    companyCountry: Yup.string().required(VALIDATION_CONSTANTS.MANDATORY),
    companyHouseNumber: Yup.string()
      .required(VALIDATION_CONSTANTS.MANDATORY)
      .min(1, VALIDATION_CONSTANTS.TOO_SHORT)
      .max(EIV_CHAR_LENGTH.BUERO_HAUSNUMMER, VALIDATION_CONSTANTS.TOO_LONG),
    companyPostal: Yup.lazy((value, context) => {
      if (context?.parent?.companyCountry === 'DE') {
        return Yup.string()
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .min(5, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.BUERO_PLZ, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string()
        .required(VALIDATION_CONSTANTS.MANDATORY)
        .min(4, VALIDATION_CONSTANTS.TOO_SHORT)
        .max(EIV_CHAR_LENGTH.BUERO_PLZ, VALIDATION_CONSTANTS.TOO_LONG);
    }),

    companyCity: Yup.string()
      .required(VALIDATION_CONSTANTS.MANDATORY)
      .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
      .max(EIV_CHAR_LENGTH.BUERO_ORT, VALIDATION_CONSTANTS.TOO_LONG),
    companyPostalCity: Yup.lazy((value, context) => {
      if (context?.parent?.companyCountry === 'DE') {
        return Yup.string().required(VALIDATION_CONSTANTS.MANDATORY);
      }
      return Yup.string().nullable().optional();
    }),
    companyStreetId: Yup.lazy((value, context) => {
      if (context?.parent?.companyCountry === 'DE') {
        return Yup.string().required(VALIDATION_CONSTANTS.MANDATORY);
      }
      return Yup.string().nullable().optional();
    }),
    companyRegionId: Yup.lazy((value, context) => {
      if (context?.parent?.companyCountry === 'DE' && value !== undefined && value !== '') {
        return Yup.string().min(1, VALIDATION_CONSTANTS.TOO_SHORT);
      }
      return Yup.string().nullable().optional();
    }),
    companyRegion: Yup.lazy((value, context) => {
      if (value !== undefined && value !== '') {
        return Yup.string()
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.BUERO_ORTSTEIL, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    companyHouseNumberAddition: Yup.lazy((value) => {
      if (value !== undefined && value !== '') {
        return Yup.string()
          .min(1, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.BUERO_HAUSNUMMERZUSATZ || 10, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    mobile: Yup.lazy((value) => {
      if (value !== undefined && value !== '') {
        return YPhoneNumber;
      }
      return Yup.string()
        .nullable()
        .optional()
        .test('mobile-or-phone-required', VALIDATION_CONSTANTS.MANDATORY, function (val) {
          return !!(val || this.parent.phone);
        });
    }),
    phone: Yup.lazy((value) => {
      if (value !== undefined && value !== '') {
        return YPhoneNumber;
      }
      return Yup.string()
        .nullable()
        .optional()
        .test('mobile-or-phone-required', VALIDATION_CONSTANTS.MANDATORY, function (val) {
          return !!(val || this.parent.mobile);
        });
    }),
    craftsChamberArea: Yup.lazy(() => {
      if (
        TENANT_ID.BAG === getTenantId() &&
        !isRegisteredInstallerGuestOnly(installerStatusSummary)
      ) {
        if (isRegisteredInstallerOnlyHilfsbetrieb(installerStatusSummary)) {
          return Yup.number();
        }
        return Yup.number().required(VALIDATION_CONSTANTS.MANDATORY);
      }
      return Yup.string().nullable().optional();
    }),

    craftsChamberNumber: Yup.lazy(() => {
      if (
        TENANT_ID.BAG === getTenantId() &&
        !isRegisteredInstallerGuestOnly(installerStatusSummary)
      ) {
        if (isRegisteredInstallerOnlyHilfsbetrieb(installerStatusSummary)) {
          return Yup.string()
            .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
            .max(EIV_CHAR_LENGTH.BUERO_STRASSE, VALIDATION_CONSTANTS.TOO_LONG);
        }
        return Yup.string()
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.BUERO_STRASSE, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    craftsChamberDate: Yup.lazy(() => {
      if (
        TENANT_ID.BAG === getTenantId() &&
        !isRegisteredInstallerGuestOnly(installerStatusSummary)
      ) {
        if (isRegisteredInstallerOnlyHilfsbetrieb(installerStatusSummary)) {
          return YDate.optional();
        }
        return YDateRequired;
      }
      return Yup.string().nullable().optional();
    }),

    businessRegistrationLocation: Yup.lazy((value) => {
      if (
        TENANT_ID.BAG === getTenantId() &&
        !isRegisteredInstallerGuestOnly(installerStatusSummary)
      ) {
        if (isRegisteredInstallerOnlyHilfsbetrieb(installerStatusSummary)) {
          if (value !== undefined && value !== '') {
            return Yup.string()
              .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
              .max(EIV_CHAR_LENGTH.BUERO_STRASSE, VALIDATION_CONSTANTS.TOO_LONG);
          }
          return Yup.string().nullable().optional();
        }
        return Yup.string()
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.BUERO_STRASSE, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    businessRegistrationDate: Yup.lazy((value) => {
      if (
        TENANT_ID.BAG === getTenantId() &&
        !isRegisteredInstallerGuestOnly(installerStatusSummary)
      ) {
        if (isRegisteredInstallerOnlyHilfsbetrieb(installerStatusSummary)) {
          if (value !== undefined && value !== '') {
            return YDate;
          }
          return Yup.string().nullable().optional();
        }
        return YDateRequired;
      }
      return Yup.string().nullable().optional();
    }),
    commercialRegisterDate: Yup.lazy((value) => {
      if (
        TENANT_ID.BAG === getTenantId() &&
        !isRegisteredInstallerGuestOnly(installerStatusSummary) &&
        value !== undefined &&
        value !== ''
      ) {
        return YDate.optional();
      }
      return Yup.string().nullable().optional();
    }),
    commercialRegisterLocation: Yup.lazy((value) => {
      if (
        TENANT_ID.BAG === getTenantId() &&
        !isRegisteredInstallerGuestOnly(installerStatusSummary) &&
        value !== undefined &&
        value !== ''
      ) {
        return Yup.string()
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.BUERO_STRASSE, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    commercialRegisterNumber: Yup.lazy((value) => {
      if (
        TENANT_ID.BAG === getTenantId() &&
        !isRegisteredInstallerGuestOnly(installerStatusSummary) &&
        value !== undefined &&
        value !== ''
      ) {
        return Yup.string()
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.BUERO_STRASSE, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    seals: Yup.array().of(YSeals),
    doesEnergyBranchExist: Yup.bool().required(VALIDATION_CONSTANTS.MANDATORY),
    doesGasBranchExist: Yup.bool().required(VALIDATION_CONSTANTS.MANDATORY),
    doesNeedAvaGuestPostal: Yup.bool().required(VALIDATION_CONSTANTS.MANDATORY),
    avaGuestPostal: Yup.lazy(() => {
      if (doesNeedAvaGuestPostal) {
        return YZipRequired;
      }
      return Yup.string().nullable().optional();
    }),

    gridProviderEnergy: Yup.lazy(() => {
      if (
        doesRegisteredInstallerHaveBranch(BRANCH_TYPE.ENERGY, installerStatusSummary) &&
        isRegisteredBranchGuest(installerStatusSummary, BRANCH_TYPE.ENERGY)
      ) {
        return Yup.number().required(VALIDATION_CONSTANTS.MANDATORY);
      }
      return Yup.string().nullable().optional();
    }),
    foreignCompanyIdEnergy: Yup.lazy(() => {
      if (
        doesRegisteredInstallerHaveBranch(BRANCH_TYPE.ENERGY, installerStatusSummary) &&
        isRegisteredBranchGuest(installerStatusSummary, BRANCH_TYPE.ENERGY)
      ) {
        return Yup.string()
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.AUSWEISNR_FREMD, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    gridProviderGas: Yup.lazy(() => {
      if (
        doesRegisteredInstallerHaveBranch(BRANCH_TYPE.GAS, installerStatusSummary) &&
        isRegisteredBranchGuest(installerStatusSummary, BRANCH_TYPE.GAS)
      ) {
        return Yup.number().required(VALIDATION_CONSTANTS.MANDATORY);
      }
      return Yup.string().nullable().optional();
    }),
    foreignCompanyIdGas: Yup.lazy(() => {
      if (
        doesRegisteredInstallerHaveBranch(BRANCH_TYPE.GAS, installerStatusSummary) &&
        isRegisteredBranchGuest(installerStatusSummary, BRANCH_TYPE.GAS)
      ) {
        return Yup.string()
          .required(VALIDATION_CONSTANTS.MANDATORY)
          .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
          .max(EIV_CHAR_LENGTH.AUSWEISNR_FREMD, VALIDATION_CONSTANTS.TOO_LONG);
      }
      return Yup.string().nullable().optional();
    }),
    // contracts: Yup.array().of(YContract), NOTE: See YContract.js
    experts: Yup.array().of(YExpert),
    gasAgreement: Yup.bool().when('doesGasBranchExist', {
      is: (doesGasBranchExist) => doesGasBranchExist === true,
      then: Yup.bool().oneOf([true], VALIDATION_CONSTANTS.MANDATORY),
    }),
    energyAgreement: Yup.bool().when('doesEnergyBranchExist', {
      is: (doesEnergyBranchExist) => doesEnergyBranchExist === true,
      then: Yup.bool().oneOf([true], VALIDATION_CONSTANTS.MANDATORY),
    }),
  });
